import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Badge,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  useMediaQuery,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications,
  AccountCircle,
  // HelpCenter,
  Search,
  Language,
} from "@mui/icons-material";
import { useState } from "react";
import LogoutMenuItem from "./LogoutMenuItem";
import NotificationPopover from "./NotificationPopover";
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import InputBase from "@mui/material/InputBase";
import { decryptData } from "utils/crypto";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const SearchBar = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "20ch",
    },
  },
}));

const DashboardAppBar = ({ handleDrawerToggle, sidebarOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElNotif, setAnchorElNotif] = useState(null);
  const open = Boolean(anchorEl);
  const opennotif = Boolean(anchorElNotif);
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("i18nextLng") || "en";
  });
  const { i18n } = useTranslation();

  let avatarSrc = "/assets/img/default-avatar.jpg";
  try {
    const user = localStorage.getItem("user");
    const decryptedUser = decryptData(user);
    if (decryptedUser?.profileImageUrl) {
      avatarSrc = decryptedUser.profileImageUrl;
    }
  } catch {}

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleOpen = (event) => setAnchorElNotif(event.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    setAnchorElNotif(null);
  };

  const DRAWER_WIDTH = 280;

  const changeLanguage = (lng) => {
    setLanguage(lng);
    localStorage.setItem("i18nextLng", lng);
    i18n.changeLanguage(lng);
  };
  return (
    <AppBar
      position="fixed"
      sx={{
        width: { md: sidebarOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : "100%" },
        ml: { md: sidebarOpen ? `${DRAWER_WIDTH}px` : 0 },
        backgroundColor: "background.paper",
        color: "text.primary",
        boxShadow:
          theme.palette.mode === "light"
            ? "0px 2px 8px rgba(0, 0, 0, 0.08)"
            : "0px 2px 8px rgba(0, 0, 0, 0.5)",
      }}
    >
      <Toolbar sx={{ px: 2 }}>
        {/* Icon menu muncul di mobile, dan jika sidebar tertutup di desktop */}
        <IconButton
          color="inherit"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            mr: 2,
            display: { xs: "block", md: sidebarOpen ? "none" : "block" },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Search Bar disembunyikan di layar kecil */}
        {!isMobile && (
          <SearchBar>
            <SearchIconWrapper>
              <Search />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search…"
              inputProps={{ "aria-label": "search" }}
            />
          </SearchBar>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1.5 },
          }}
        >
          {/* Icon Notifikasi */}
          <IconButton
            component={motion.button}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            size="large"
            color="inherit"
            onClick={handleOpen}
            sx={{
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            <Badge
              badgeContent={0}
              color="error"
              overlap="circular"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.7rem",
                  height: 20,
                  minWidth: 20,
                  animation: "pulse 1.5s infinite",
                },
              }}
            >
              <Notifications sx={{ color: theme.palette.primary.main }} />
            </Badge>
          </IconButton>

          <NotificationPopover
            open={opennotif}
            anchorEl={anchorElNotif}
            onClose={handleClose}
          />

          {/* Language Selector */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={language}
              onChange={(e) => changeLanguage(e.target.value)}
              sx={{
                "& .MuiSelect-select": {
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Language
                    sx={{ color: theme.palette.primary.main, fontSize: 20 }}
                  />
                  <Typography variant="body2" textTransform="uppercase">
                    {selected}
                  </Typography>
                </Box>
              )}
            >
              <MenuItem value="en">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <img
                    src="https://flagcdn.com/w20/gb.png"
                    alt="English"
                    width="20"
                  />
                  <Typography variant="body2">English</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="id">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <img
                    src="https://flagcdn.com/w20/id.png"
                    alt="Indonesia"
                    width="20"
                  />
                  <Typography variant="body2">Indonesia</Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Avatar User */}
          <IconButton
            size="large"
            edge="end"
            onClick={handleMenu}
            color="inherit"
            sx={{
              p: 0,
              "&:hover": {
                transform: "scale(1.1)",
                transition: "transform 0.3s ease",
              },
            }}
          >
            <Avatar
              alt="User Avatar"
              src={avatarSrc}
              sx={{
                width: 36,
                height: 36,
                bgcolor: "background.default",
                objectFit: "cover",
              }}
            />
          </IconButton>
        </Box>

        {/* Menu User */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            elevation: 3,
            sx: {
              width: 200,
              borderRadius: 2,
              mt: 1.5,
              overflow: "visible",
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
        >
          <MenuItem
            component={Link}
            to="/dashboard/profile"
            onClick={handleClose}
          >
            <ListItemIcon>
              <AccountCircle fontSize="small" />
            </ListItemIcon>
            <ListItemText>Profile</ListItemText>
          </MenuItem>

          <Divider sx={{ my: 1 }} />

          {/* <MenuItem onClick={handleClose}>
            <ListItemIcon>
              <HelpCenter fontSize="small" />
            </ListItemIcon>
            <ListItemText>Help Center</ListItemText>
          </MenuItem> */}

          <LogoutMenuItem />
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default DashboardAppBar;
